import type {
	CoursesAskMentorDto,
	CoursesCommunityCourseQueryDto,
	CoursesCreateDiscussionDto,
	CoursesSendSupportDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	ListAmaInput,
	ListAmaOutput,
	AskMentorInput,
	AskMentorOutput,
	ListDiscussionsInput,
	ListDiscussionsOutput,
	CreateDiscussionInput,
	CreateDiscussionOutput,
	ToggleAmaHelpfulInput,
	ToggleAmaHelpfulOutput,
	SendSupportInput,
	SendSupportOutput,
} from '../../types/community.types';

const TAG = 'courses:community' as const;
const INVALIDATES = [TAG, 'courses:discussions'] as const;

export const communityEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		listAma: builder.query<ListAmaOutput, ListAmaInput>({
			query: (params) => ({
				url: 'courses/community/ama',
				params: params satisfies Wire<CoursesCommunityCourseQueryDto>,
			}),
			providesTags: [TAG],
		}),
		askMentor: builder.mutation<AskMentorOutput, AskMentorInput>({
			query: (body) => ({
				url: 'courses/community/ama',
				method: 'POST',
				body: body satisfies Wire<CoursesAskMentorDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		listDiscussions: builder.query<ListDiscussionsOutput, ListDiscussionsInput>({
			query: (params) => ({
				url: 'courses/community/discussions',
				params: params satisfies Wire<CoursesCommunityCourseQueryDto>,
			}),
			providesTags: [TAG],
		}),
		createDiscussion: builder.mutation<CreateDiscussionOutput, CreateDiscussionInput>({
			query: (body) => ({
				url: 'courses/community/discussions',
				method: 'POST',
				body: body satisfies Wire<CoursesCreateDiscussionDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		toggleAmaHelpful: builder.mutation<ToggleAmaHelpfulOutput, ToggleAmaHelpfulInput>({
			query: ({ questionId }) => ({
				url: `courses/community/ama/${encodeURIComponent(questionId)}/helpful`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		sendSupport: builder.mutation<SendSupportOutput, SendSupportInput>({
			query: (body) => ({
				url: 'courses/community/support',
				method: 'POST',
				body: body satisfies Wire<CoursesSendSupportDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useListAmaQuery,
	useLazyListAmaQuery,
	useAskMentorMutation,
	useListDiscussionsQuery,
	useLazyListDiscussionsQuery,
	useCreateDiscussionMutation,
	useToggleAmaHelpfulMutation,
	useSendSupportMutation,
} = communityEndpoints;
